/**
 * Stories handled by ZillowNavbar
 */
/* eslint-disable local-rules/require-storybook-documentation */
import {
    FunctionComponent,
    useState,
    useRef,
    useCallback,
    MouseEventHandler,
    MouseEvent as ReactMouseEvent,
} from 'react';
import { HeaderSection } from 'src/components/ZillowNavbar/page-frame-types/HeaderSection';
import { HeaderLink } from 'src/components/ZillowNavbar/page-frame-types/HeaderLink';
import { HEADER_VERSIONS, NavbarSectionTestID } from 'src/components/ZillowNavbar/constants';
import { HeaderLinkDescription } from 'src/components/ZillowNavbar/page-frame-types/HeaderLinkDescription';
import { ZillowNavbarNotification } from '../../../ZillowNavbarNotification';
import { ZillowNavbarSectionContainer } from './components/ZillowNavbarSectionContainer';
import { ZillowNavbarLinkAnchor } from './components/ZillowNavbarLinkAnchor';
import { MINIMUM_WIDTH_OF_DROPDOWN_IN_PX } from './components/ZillowNavbarSectionDesktopMegaMenu/components/ZillowNavbarSectionMegaMenuContainer';
import { ZillowNavbarSectionMegaMenu } from './components/ZillowNavbarSectionDesktopMegaMenu';

type ZillowNavbarSectionProps = {
    section: HeaderSection;
    useVerticalMegaMenu: boolean;
    zhlHost: string;
    version: HEADER_VERSIONS;
    onLinkClick?: (
        link: HeaderLink | HeaderLinkDescription,
        event: ReactMouseEvent<HTMLAnchorElement, MouseEvent>
    ) => void;
};

const OPENING_DELAY_IN_MS = 200;
// Take slightly longer to close so that we don't flash the underlying content when the user pans between mega menus
const CLOSING_DELAY_IN_MS = OPENING_DELAY_IN_MS + 100;
export const ZillowNavbarSection: FunctionComponent<ZillowNavbarSectionProps> = ({
    section,
    version,
    useVerticalMegaMenu,
    zhlHost,
    onLinkClick,
}) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isSectionLinkHovered, setIsSectionLinkHovered] = useState(false);
    const [isVerticalMegaMenuLeftAligned, setIsVerticalMegaMenuLeftAligned] = useState(false);
    const openingTimeout = useRef<NodeJS.Timeout | null>(null);
    const closingTimeout = useRef<NodeJS.Timeout | null>(null);

    const onMouseEnter: MouseEventHandler<HTMLLIElement> = useCallback(event => {
        setIsSectionLinkHovered(true);
        // if closing timeout is available, cancel it since the mouse has reentered the megamenu
        if (closingTimeout.current) {
            clearTimeout(closingTimeout.current);
        }
        openingTimeout.current = setTimeout(() => {
            setIsHovered(true);
            setIsVerticalMegaMenuLeftAligned(event.currentTarget?.offsetLeft < MINIMUM_WIDTH_OF_DROPDOWN_IN_PX);
            openingTimeout.current = null;
        }, OPENING_DELAY_IN_MS);
    }, []);

    const onMegaMenuMouseEnter: MouseEventHandler<HTMLDivElement> = useCallback(() => {
        setIsSectionLinkHovered(false);
    }, []);

    const onMouseLeave: MouseEventHandler<HTMLLIElement> = useCallback(() => {
        setIsSectionLinkHovered(false);
        if (openingTimeout.current) {
            // immediately cancel so it doesn't open if the timeout hasn't triggered, to avoid flashing the megamenu
            clearTimeout(openingTimeout.current);
            return;
        }
        closingTimeout.current = setTimeout(() => {
            // close and reset the megamenu
            setIsHovered(false);
            setIsVerticalMegaMenuLeftAligned(false);
            closingTimeout.current = null;
        }, CLOSING_DELAY_IN_MS);
    }, []);

    const _onLinkClick = useCallback(
        (link: HeaderLink | HeaderLinkDescription, event: ReactMouseEvent<HTMLAnchorElement, MouseEvent>) => {
            if (!onLinkClick) {
                return;
            }
            onLinkClick(link, event);
        },
        [onLinkClick]
    );

    const testID = new NavbarSectionTestID(section.link.text);

    return (
        <ZillowNavbarSectionContainer
            isVertical={useVerticalMegaMenu}
            {...testID.getCypressTestIDAttribute()}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}>
            <ZillowNavbarLinkAnchor
                {...testID.anchor.getCypressTestIDAttribute()}
                isActive={isSectionLinkHovered}
                onClick={(event: ReactMouseEvent<HTMLAnchorElement, MouseEvent>) => _onLinkClick(section.link, event)}
                href={(section.link as HeaderLink).href}>
                {section.link.text}
                {!!(section.link as HeaderLink).notifications && (
                    <ZillowNavbarNotification>{(section.link as HeaderLink).notifications}</ZillowNavbarNotification>
                )}
            </ZillowNavbarLinkAnchor>
            {isHovered && !!section.subsections?.length && (
                <ZillowNavbarSectionMegaMenu
                    version={version}
                    testID={testID}
                    onLinkClick={_onLinkClick}
                    onMouseEnter={onMegaMenuMouseEnter}
                    isLeftAligned={isVerticalMegaMenuLeftAligned}
                    isVertical={useVerticalMegaMenu}
                    isHomeLoansDashboardLinkRendered={section.link.text === 'Home Loans'}
                    zhlHost={zhlHost}
                    subsections={section.subsections!}
                />
            )}
        </ZillowNavbarSectionContainer>
    );
};
